// Plans d’abonnement — catalogue API /plans

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Search } from "lucide-react";
import { toast } from "react-toastify";

import {
  AdminDataTableShell,
  AdminEmptyState,
  AdminKpiCard,
  AdminPageHero,
  adminHeroActionClassName,
} from "../../../modules/admin/ui";
import {
  deletePlanAbonnement,
  getPlansAbonnement,
  type PlanAbonnement,
  type Frequence,
} from "../../../lib/api/plansAbonnement";
import { PlansAbonnementModal } from "./PlansAbonnementModal";

const FREQ_LABEL: Record<Frequence, string> = {
  PONCTUEL: "Ponctuel",
  HEBDOMADAIRE: "Hebdomadaire",
  MENSUEL: "Mensuel",
  ANNUEL: "Annuel",
};

function badgeActif(actifListe: boolean): React.JSX.Element {
  const cls = actifListe
    ? "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30"
    : "bg-slate-600/35 text-slate-300 ring-slate-500/30";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${cls}`}>
      {actifListe ? "Actif" : "Inactif"}
    </span>
  );
}

function servicesLabel(description: string | null | undefined): string {
  const t = description?.trim();
  if (!t) return "À compléter";
  if (t.length > 72) return `${t.slice(0, 70)}…`;
  return t;
}

function dotationTypeLabel(type: string): string {
  const map: Record<string, string> = {
    POUBELLE: "Poubelle",
    BAC: "Bac",
    SACS: "Sacs",
  };
  return map[type] ?? type;
}

function serviceTypeLabel(type: string): string {
  const map: Record<string, string> = {
    NETTOYAGE: "Nettoyage",
    DESINFECTION: "Desinfection",
    LAVAGE_BAC: "Lavage bac",
  };
  return map[type] ?? type;
}

function dotationsLabel(plan: PlanAbonnement): string {
  const list = (plan.dotations ?? []).filter((d) => d.quantite > 0);
  if (!list.length) return "Aucune";
  return list
    .map((d) => `${d.resourceCatalog?.nom ?? dotationTypeLabel(d.type ?? "Article catalogue")} x${Math.max(1, Number(d.quantite ?? 1))}`)
    .join(", ");
}

function planServicesLabel(plan: PlanAbonnement): string {
  const list = (plan.services ?? []).filter((s) => s.actif !== false);
  if (!list.length) return servicesLabel(plan.description);
  return list.map((s) => serviceTypeLabel(s.type)).join(", ");
}

export default function PlansAbonnementPage(): React.JSX.Element {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<PlanAbonnement[]>([]);

  const [search, setSearch] = useState("");
  const [filterFrequence, setFilterFrequence] = useState<string>("");
  const [filterZone, setFilterZone] = useState<string>("");
  const [filterStatut, setFilterStatut] = useState<"inactive" | "all">("all");
  const [filterDevise, setFilterDevise] = useState<string>("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "duplicate">("create");
  const [modalInitial, setModalInitial] = useState<PlanAbonnement | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPlansAbonnement();
      setPlans(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erreur lors du chargement des plans.");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const zoneOptions = useMemo(() => {
    const names = new Set<string>();
    for (const p of plans) {
      const n = p.zone?.nom?.trim();
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "fr"));
  }, [plans]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans.filter((p) => {
      const zoneNom = p.zone?.nom ?? "";
      if (filterStatut === "inactive") return false;

      if (filterFrequence && p.frequence !== filterFrequence) return false;
      if (filterDevise && p.devise !== filterDevise) return false;
      if (filterZone) {
        if (filterZone === "__global__") {
          if (p.zone != null) return false;
        } else if (zoneNom !== filterZone) {
          return false;
        }
      }
      if (!q) return true;
      const blob = `${p.nom} ${FREQ_LABEL[p.frequence]} ${zoneNom} ${p.description ?? ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [plans, search, filterFrequence, filterZone, filterStatut, filterDevise]);

  const kpis = useMemo(() => {
    const plansActifs = plans.length;
    const plansInactifsLabel = "À connecter";
    const mensuels = plans.filter((p) => p.frequence === "MENSUEL").length;
    const avecZone = plans.filter((p) => p.zone != null || p.zoneId != null).length;
    const revenuHint = "Non calculé côté front (voir évolution backend).";
    return { plansActifs, plansInactifsLabel, mensuels, avecZone, revenuHint };
  }, [plans]);

  const exportCsv = () => {
    const sep = ";";
    const bom = "\uFEFF";
    const headers = ["Plan", "Fréquence", "Prix", "Devise", "Zone", "Dotations prévues", "Services inclus", "Statut"];
    const rows = filtered.map((p) => [
      `"${String(p.nom).replace(/"/g, '""')}"`,
      FREQ_LABEL[p.frequence],
      String(p.montant),
      p.devise,
      `"${(p.zone?.nom ?? "Globale").replace(/"/g, '""')}"`,
      "À structurer",
      `"${(p.description ?? "").replace(/"/g, '""')}"`,
      "Actif",
    ]);
    const body = [headers.join(sep), ...rows.map((r) => r.join(sep))].join("\n");
    const blob = new Blob([bom + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plans-abonnement-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV généré.");
  };

  const handleExportClick = () => {
    if (filtered.length === 0) {
      toast.warning("Rien à exporter sous les filtres courants.");
      return;
    }
    exportCsv();
  };

  const openCreate = () => {
    setModalMode("create");
    setModalInitial(null);
    setModalOpen(true);
  };

  const openEdit = (p: PlanAbonnement) => {
    setModalMode("edit");
    setModalInitial(p);
    setModalOpen(true);
  };

  const openDup = (p: PlanAbonnement) => {
    setModalMode("duplicate");
    setModalInitial(p);
    setModalOpen(true);
  };

  const handleDeleteSoft = async (p: PlanAbonnement) => {
    if (
      !window.confirm(`Désactiver le plan « ${p.nom} » ? Les contrats liés peuvent empêcher la suppression.`)
    ) {
      return;
    }
    try {
      await deletePlanAbonnement(p.id);
      toast.success("Plan désactivé (soft delete serveur).");
      await fetchData();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Action impossible.");
    }
  };

  const wireSoon = () => toast.info("À connecter — vue des abonnements liés après évolution API.", { position: "bottom-center" });

  const toastActiver = () =>
    toast.info("Réactivation indisponible : les plans désactivés ne sont pas renvoyés par l’API actuelle.", {
      position: "bottom-center",
    });

  const heroActions = (
    <>
      <button
        type="button"
        onClick={openCreate}
        className={adminHeroActionClassName(modalOpen)}
      >
        Nouveau plan
      </button>
      <Link
        to="/admin/contrats"
        className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        Voir abonnements
      </Link>
      <button
        type="button"
        onClick={() => handleExportClick()}
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        <FileDown className="h-4 w-4" />
        Exporter
      </button>
    </>
  );

  return (
    <div className="space-y-5 text-slate-100">
        <AdminPageHero
          eyebrow="Catalogue commercial"
          title="Plans d’abonnement"
          subtitle="Catalogue des offres utilisées pour créer les contrats clients."
          actions={heroActions}
        />

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <AdminKpiCard label="Plans actifs" value={String(kpis.plansActifs)} hint="GET /plans : plans actifs uniquement" />
          <AdminKpiCard
            label="Plans inactifs"
            value={kpis.plansInactifsLabel}
            hint="Non exposé par GET /plans"
          />
          <AdminKpiCard label="Plans mensuels" value={String(kpis.mensuels)} />
          <AdminKpiCard label="Plans liés à une zone" value={String(kpis.avecZone)} />
          <AdminKpiCard label="Revenu théorique mensuel" value="À connecter" hint={kpis.revenuHint} />
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 shadow-lg backdrop-blur-sm md:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                placeholder="Rechercher un plan, une zone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950/60 py-2.5 pl-10 pr-3 text-sm text-white outline-none ring-emerald-500/30 placeholder:text-slate-500 focus:border-emerald-500/50 focus:ring-2"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={filterFrequence}
                onChange={(e) => setFilterFrequence(e.target.value)}
                className="rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2"
              >
                <option value="">Fréquence : toutes</option>
                <option value="PONCTUEL">Ponctuel</option>
                <option value="HEBDOMADAIRE">Hebdomadaire</option>
                <option value="MENSUEL">Mensuel</option>
                <option value="ANNUEL">Annuel</option>
              </select>
              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                className="min-w-[10rem] rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2"
              >
                <option value="">Zone : toutes</option>
                <option value="__global__">Sans zone (globale)</option>
                {zoneOptions.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
              <select
                value={filterStatut}
                onChange={(e) => setFilterStatut(e.target.value as "inactive" | "all")}
                className="rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2"
              >
                <option value="all">Statut : tous (actifs côté API)</option>
                <option value="inactive">Inactifs uniquement — liste vide</option>
              </select>
              <select
                value={filterDevise}
                onChange={(e) => setFilterDevise(e.target.value)}
                className="rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2"
              >
                <option value="">Devise : toutes</option>
                <option value="USD">USD</option>
                <option value="CDF">CDF</option>
              </select>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Filtre « inactifs » : l’API actuelle ne renvoie pas les plans désactivés — utilisable pour vérif UX jusqu’à un endpoint liste complète.
          </p>
        </section>

        {loading ? (
          <p className="rounded-2xl border border-slate-800 bg-slate-900/40 py-16 text-center text-sm text-slate-400">
            Chargement du catalogue…
          </p>
        ) : plans.length === 0 ? (
          <AdminDataTableShell>
            <AdminEmptyState
              title="Aucun plan d’abonnement configuré"
              description="Créez vos premières offres : Ménage Standard, Ménage Premium, Professionnel ou Chantier."
              action={
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={openCreate}
                    className={adminHeroActionClassName(modalOpen)}
                  >
                    Créer un plan
                  </button>
                  <Link
                    to="/admin/contrats"
                    className="rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-slate-800"
                  >
                    Voir la page Abonnements
                  </Link>
                </div>
              }
            />
          </AdminDataTableShell>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleExportClick()}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/15"
              >
                <FileDown className="h-4 w-4" />
                Exporter (CSV filtres courants)
              </button>
            </div>

            {filterStatut === "inactive" ? (
              <AdminDataTableShell>
                <AdminEmptyState
                  title="Aucun plan inactif dans cette liste"
                  description="L’endpoint GET /plans ne renvoie que les offres actives. Réactivation ou inventaire complet demandera une évolution backend."
                  action={
                    <button type="button" onClick={() => setFilterStatut("all")} className="rounded-xl border border-slate-600 px-4 py-2 text-sm font-medium">
                      Revenir aux plans actifs
                    </button>
                  }
                />
              </AdminDataTableShell>
            ) : filtered.length === 0 ? (
              <AdminDataTableShell>
                <div className="py-14 text-center text-sm text-slate-400">
                  Aucun résultat pour ces filtres. Réinitialisez la recherche.
                </div>
              </AdminDataTableShell>
            ) : (
              <AdminDataTableShell>
                <div className="overflow-x-auto">
                  <table className="min-w-[980px] w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-3">Plan</th>
                        <th className="px-4 py-3">Fréquence</th>
                        <th className="px-4 py-3">Prix</th>
                        <th className="px-4 py-3">Devise</th>
                        <th className="px-4 py-3">Zone</th>
                        <th className="px-4 py-3">Dotations prévues</th>
                        <th className="px-4 py-3">Services inclus</th>
                        <th className="px-4 py-3">Statut</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr key={p.id} className="border-b border-slate-800/80 hover:bg-slate-900/60">
                          <td className="max-w-[12rem] px-4 py-3 font-medium text-white">{p.nom}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-300">{FREQ_LABEL[p.frequence]}</td>
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-100">
                            {Number(p.montant).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-300">{p.devise}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                            {p.zone?.nom ? `Zone : ${p.zone.nom}` : "Global"}
                          </td>
                          <td className="max-w-[16rem] px-4 py-3 text-xs text-amber-200/90" title={dotationsLabel(p)}>
                            {dotationsLabel(p)}
                          </td>
                          <td className="max-w-[14rem] px-4 py-3 text-xs text-slate-400" title={p.description ?? ""}>
                            {planServicesLabel(p)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">{badgeActif(true)}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => openEdit(p)}
                                className="rounded-lg border border-slate-700 bg-slate-950/60 px-2 py-1 text-[11px] font-medium text-slate-200 hover:bg-slate-800"
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => toastActiver()}
                                className="rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-800"
                                title="Non supporté par l’API listant uniquement les plans actifs"
                              >
                                Activer
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteSoft(p)}
                                className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-100 hover:bg-amber-500/15"
                              >
                                Désactiver
                              </button>
                              <button
                                type="button"
                                onClick={() => wireSoon()}
                                className="rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-400 hover:bg-slate-800"
                              >
                                Liés abonnements
                              </button>
                              <button
                                type="button"
                                onClick={() => openDup(p)}
                                className="rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
                              >
                                Dupliquer
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteSoft(p)}
                                className="rounded-lg border border-red-500/35 bg-red-500/10 px-2 py-1 text-[11px] font-medium text-red-200 hover:bg-red-500/15"
                                title="Désactivation serveur si aucune utilisation métier"
                              >
                                Supprimer
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminDataTableShell>
            )}
          </>
        )}

      {modalOpen ? (
        <PlansAbonnementModal
          open={modalOpen}
          mode={modalMode}
          initial={modalInitial}
          onClose={() => setModalOpen(false)}
          onSaved={() => void fetchData()}
        />
      ) : null}
    </div>
  );
}
